import { z } from 'zod';
import { Button, Modal } from 'react-bootstrap';
import { Typography } from 'Components/atoms';
import { AiOutlineClose } from '@react-icons/all-files/ai/AiOutlineClose';
import { useContext, useEffect, useState } from 'react';
import { CREDENTIALS_SAVED, ERROR_MESSAGE, ERROR_VARIANT, SUCCESS_VARIANT } from 'Core-utils/constants/constants';
import { getApi } from 'Src/Api';
import InputField from 'Components/molecules/InputField/InputField';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import './styles.scss';

type CredentialsScreenData = {
    label: string;
    placeholder: string;
    value?: string;
};

const FIELDS_DATA: CredentialsScreenData[] = [
    {
        label: 'Token URL',
        placeholder: 'Token URL',
        value: undefined,
    },
    {
        label: 'Client ID',
        placeholder: 'Client ID',
        value: undefined,
    },
    {
        label: 'Client Secret',
        placeholder: 'Client Secret',
        value: undefined,
    },
];

export interface ComplianceBenchmarksCredentialsModalProps {
    cid: string;
    show: boolean;
    onClose: () => void;
}

const isValid = (fieldData: CredentialsScreenData[]) => {
    const tokenUrl = fieldData.find((field) => field.label === 'Token URL')?.value || undefined;
    const tokenUrlSchema = z.string().url();
    if (!tokenUrl || !tokenUrlSchema.safeParse(tokenUrl).success) {
        return false;
    }
    const clientId = fieldData.find((field) => field.label === 'Client ID')?.value || undefined;
    const clientSecret = fieldData.find((field) => field.label === 'Client Secret')?.value || undefined;
    const nonEmptyStringSchema = z.string().min(1);
    if (
        !clientId ||
        !clientSecret ||
        !nonEmptyStringSchema.safeParse(clientId).success ||
        !nonEmptyStringSchema.safeParse(clientSecret).success
    ) {
        return false;
    }
    return true;
};

const getPatchCredentialsPayload = (fieldData: CredentialsScreenData[]) => {
    const isValidData = isValid(fieldData);
    if (!isValidData) {
        return undefined;
    }
    const clientId = fieldData.find((field) => field.label === 'Client ID')?.value || '';
    const clientSecret = fieldData.find((field) => field.label === 'Client Secret')?.value || '';
    const tokenUrl = fieldData.find((field) => field.label === 'Token URL')?.value || '';
    const payload = {
        complianceBenchmarkCredentials: {
            clientId,
            clientSecret,
            tokenUrl,
        },
    };
    return payload;
};

const handleCredentialsPatch = async (cid: string, fieldData: CredentialsScreenData[]) => {
    const payload = getPatchCredentialsPayload(fieldData);
    if (!payload) {
        return;
    }
    const api = await getApi();

    const res = await api?.install.credentialsPatch(cid, payload);
    if (res) {
        return { success: res };
    }
    return { success: false };
};

const ComplianceBenchmarksCredentialsModal = ({ cid, show, onClose }: ComplianceBenchmarksCredentialsModalProps) => {
    const modalClassName = `custom-width-default`;
    const { addToast } = useContext(ToastsContext);
    const [updatedText, setUpdatedText] = useState<CredentialsScreenData[]>(FIELDS_DATA);
    const [clicked, setClicked] = useState(false);
    useEffect(() => {
        if (clicked) {
            handleCredentialsPatch(cid, updatedText)
                .then((res) => {
                    if (res?.success) {
                        addToast({ severity: SUCCESS_VARIANT, body: CREDENTIALS_SAVED });
                    } else {
                        addToast({ severity: ERROR_VARIANT, body: ERROR_MESSAGE });
                    }
                })
                .catch((_) => {
                    addToast({ severity: ERROR_VARIANT, body: ERROR_MESSAGE });
                });
            setClicked(false);
            onClose();
        }
    }, [clicked]);

    const handleChange = (index: number, newValue: string) => {
        const updatedTextCopy = [...updatedText];
        updatedTextCopy[index] = { ...updatedTextCopy[index], value: newValue };
        setUpdatedText(updatedTextCopy);
    };
    const handleSave = () => {
        setClicked(true);
    };
    const isDisabled = isValid(updatedText) ? false : true;
    const title = 'Configure Compliance Benchmarks Credentials';
    return (
        <Modal show={show} className="test-engagement-modal" centered dialogClassName={modalClassName} onHide={onClose}>
            <Modal.Header>
                <Modal.Title className="text-uppercase">
                    <Typography variant="subtitle4" color="text-high-emphasis">
                        {title}
                    </Typography>
                </Modal.Title>
                <AiOutlineClose className="text-text-high-emphasis confirmation-modal__icon" onClick={onClose} />
            </Modal.Header>
            <Modal.Body>
                <div className="credentials-screen-modal pad-4">
                    <div className="d-flex flex-column gap-4">
                        {updatedText.map((dataItem, index) => (
                            <InputField
                                key={index}
                                label={dataItem.label}
                                placeholder={dataItem.placeholder}
                                value={dataItem.value}
                                onValueChange={(newValue: string) => handleChange(index, newValue)}
                                variant="encrypted"
                            />
                        ))}
                    </div>
                    <div className="mt-4 border-0 d-flex justify-content-start">
                        <Button className="credentials-save-btn" onClick={handleSave} disabled={isDisabled || clicked}>
                            <Typography variant="body1">{'Save/Update'}</Typography>
                        </Button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default ComplianceBenchmarksCredentialsModal;
